import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { getCountryName } from '../../../utils/countries';
import { formatSupplierCategory } from '../../../utils/formatters';
import { API_BASE_URL } from '../../../config/api';
import { useToast } from '../../../components/ui/Toast';

const VendorHeader = ({ vendor, onEdit, onStatusChange, userRole }) => {
  const toast = useToast();
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const getStatusVariant = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'under_review':
        return 'info';
      case 'rejected':
        return 'destructive';
      case 'inactive':
        return 'secondary';
      case 'suspended':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  // Export functionality (Excel only)
  const handleExportData = async (format) => {
    try {
      setShowExportDropdown(false);
      
      if (format !== 'excel') return;
      const endpoint = `${API_BASE_URL}/vendors/${vendor.id}/export/excel`;
      
      // Default filename using company name or vendor code
      const defaultFilename = `${(vendor.company_name || vendor.vendor_code || 'vendor').replace(/[^a-zA-Z0-9]/g, '_')}_export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Call backend API to generate file
      // Use credentials: 'include' to send httpOnly cookies for authentication
      const response = await fetch(endpoint, { 
        method: 'GET',
        credentials: 'include', // Important: sends httpOnly cookies
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast.error('Authentication required. Please log in.');
          window.location.href = '/company-user-login';
          return;
        }
        const txt = await response.text();
        toast.error('Export failed: ' + (txt || `${response.status} ${response.statusText}`));
        throw new Error(txt || `Export failed: ${response.status} ${response.statusText}`);
      }

      // Get the file blob
      const fileBlob = await response.blob();
      
      // Get filename from response headers
      let filename = defaultFilename;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        // Try multiple patterns to extract filename
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const url = URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message (you can replace this with a toast notification)
      console.log(`EXCEL export completed successfully: ${filename}`);
      
    } catch (error) {
      console.error('Export failed:', error);
      
      // No fallback
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm bg-white">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Vendor Basic Info */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Building2" size={28} className="text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900 truncate">
                  {vendor.company_name}
                </h1>
                <Badge variant={getStatusVariant(vendor.status)}>
                  {vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1).replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="Hash" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Code:</span>
                  <span className="font-semibold text-slate-900">{vendor.vendor_code || 'Pending'}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="Mail" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Email:</span>
                  <span className="font-semibold text-slate-900 truncate">{vendor.email}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="Phone" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Phone:</span>
                  <span className="font-semibold text-slate-900">{vendor.phone_number}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="MapPin" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Location:</span>
                  <span className="font-semibold text-slate-900">{vendor.registered_city}, {getCountryName(vendor.country_origin)}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="Calendar" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Registered:</span>
                  <span className="font-semibold text-slate-900">{new Date(vendor.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                  <Icon name="Tag" size={16} className="text-slate-600" />
                  <span className="text-slate-600 font-medium">Category:</span>
                  <span className="font-semibold text-slate-900">{formatSupplierCategory(vendor.supplier_category)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Controls */}
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-auto items-start lg:items-end">
            {(userRole === 'Admin' || userRole === 'Approver') && (
              <>
                {(vendor.status?.toLowerCase() === 'pending' && !vendor.vendor_code) && (
                  <Button
                    variant="default"
                    onClick={onEdit}
                    iconName="Hash"
                    iconPosition="left"
                  >
                    Assign Vendor Code
                  </Button>
                )}
                {/* Active/Inactive Toggle */}
                {['active','inactive'].includes((vendor.status || '').toLowerCase()) && (
                  <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 rounded-lg px-4 py-2.5">
                    <span className="text-sm font-semibold text-slate-700">Status</span>
                    <button
                      type="button"
                      aria-pressed={(vendor.status || '').toLowerCase() === 'active'}
                      onClick={() => {
                        const isActive = (vendor.status || '').toLowerCase() === 'active';
                        onStatusChange(isActive ? 'inactive' : 'active');
                      }}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 ${
                        (vendor.status || '').toLowerCase() === 'active' 
                          ? 'bg-emerald-500' 
                          : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                          (vendor.status || '').toLowerCase() === 'active' ? 'translate-x-8' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm font-semibold text-slate-900">
                      {(vendor.status || '').toLowerCase() === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                )}
              </>
            )}
            
            {/* Export Dropdown (Excel only) */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                iconName="FileSpreadsheet"
                iconPosition="left"
              >
                Export
              </Button>
              
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-md z-10 overflow-hidden">
                  <div className="p-1">
                    <button
                      onClick={() => handleExportData('excel')}
                      className="w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors flex items-center gap-2"
                    >
                      <Icon name="FileSpreadsheet" size={16} />
                      <span>Export as Excel</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Click outside to close dropdown */}
        {showExportDropdown && (
          <div 
            className="fixed inset-0 z-0" 
            onClick={() => setShowExportDropdown(false)}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default VendorHeader;